import React, { useEffect, useState, useCallback } from 'react';
import { Package, Heart, Gift, Shield, MapPin } from 'lucide-react';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import OrdersSection from '../components/dashboard/OrdersSection';
import WishlistSection from '../components/dashboard/WishlistSection';
import AddressSection from '../components/dashboard/AddressSection';
import SecuritySection from '../components/dashboard/SecuritySection';

const CANCELLATION_REASONS = [
  "Changed my mind", "Found better price elsewhere", "Ordered by mistake",
  "Delivery taking too long", "Want to change order details", "Financial reasons", "Other"
];

export default function CustomerDashboard() {
  const [orders, setOrders] = useState([]);
  const [wishlist, setWishlist] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [rewardPoints, setRewardPoints] = useState({ points: 0, settings: null });
  const [activeTab, setActiveTab] = useState('orders');
  const { user, loading, changePassword: authChangePassword } = useAuth();
  const navigate = useNavigate();
  const [cancelModal, setCancelModal] = useState({ isOpen: false, orderId: null, reason: '', details: '' });
  const [passwordForm, setPasswordForm] = useState({ current: '', new_password: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new_password: false, confirm: false });
  const [changingPassword, setChangingPassword] = useState(false);
  const [savedAddress, setSavedAddress] = useState({ name: '', phone: '', street: '', city: '', state: '', pincode: '' });
  const [savingAddress, setSavingAddress] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, wishlistRes, pointsRes, sessionsRes] = await Promise.all([
        api.get('/orders/my-orders'), api.get('/wishlist'),
        api.get('/user/reward-points'), api.get('/auth/sessions')
      ]);
      setOrders(ordersRes.data); setWishlist(wishlistRes.data);
      setRewardPoints(pointsRes.data); setSessions(sessionsRes.data.sessions || []);
    } catch (error) { /* Failed to load dashboard data */ }
    try {
      const addrRes = await api.get('/user/addresses');
      if (addrRes.data.shipping_address) setSavedAddress(addrRes.data.shipping_address);
    } catch (e) { /* No saved address yet */ }
  }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate('/login'); return; }
    fetchData();
  }, [user, loading, navigate, fetchData]);

  const removeFromWishlist = async (productId) => {
    try {
      await api.delete(`/wishlist/${productId}`);
      setWishlist(wishlist.filter(p => p.id !== productId));
      toast.success('Removed from wishlist');
    } catch (error) { toast.error('Failed to remove from wishlist'); }
  };

  const canRequestCancellation = (order) => {
    return !order.cancellation_requested && ['pending', 'confirmed', 'processing'].includes(order.order_status);
  };

  const openCancelModal = (orderId) => setCancelModal({ isOpen: true, orderId, reason: '', details: '' });

  const submitCancellationRequest = async () => {
    if (!cancelModal.reason) { toast.error('Please select a reason'); return; }
    try {
      await api.post(`/orders/${cancelModal.orderId}/request-cancellation`, { reason: cancelModal.reason, details: cancelModal.details });
      toast.success('Cancellation request submitted');
      setCancelModal({ isOpen: false, orderId: null, reason: '', details: '' });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to submit request'); }
  };

  const handleSaveAddress = async (e) => {
    e.preventDefault();
    setSavingAddress(true);
    try {
      await api.post('/user/addresses', { shipping_address: savedAddress });
      toast.success('Address saved');
    } catch (error) { toast.error('Failed to save address'); }
    finally { setSavingAddress(false); }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm) { toast.error('Passwords do not match'); return; }
    if (passwordForm.new_password.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setChangingPassword(true);
    try {
      await authChangePassword(passwordForm.current, passwordForm.new_password);
      toast.success('Password changed. Please log in again.');
      navigate('/login');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to change password'); }
    finally { setChangingPassword(false); }
  };

  const revokeSession = async (sessionId) => {
    try {
      await api.delete(`/auth/sessions/${sessionId}`);
      setSessions(sessions.filter(s => s.session_id !== sessionId));
      toast.success('Session revoked');
    } catch (error) { toast.error('Failed to revoke session'); }
  };

  const revokeAllOtherSessions = async () => {
    try {
      await api.delete('/auth/sessions');
      setSessions(sessions.filter(s => s.is_current));
      toast.success('All other sessions revoked');
    } catch (error) { toast.error('Failed to revoke sessions'); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin h-12 w-12 border-4 border-[#1a3a5c] border-t-transparent rounded-full" /></div>;
  if (!user) return null;

  const tabs = [
    { id: 'orders', icon: Package, label: 'Orders' },
    { id: 'wishlist', icon: Heart, label: `Wishlist (${wishlist.length})` },
    { id: 'addresses', icon: MapPin, label: 'Addresses' },
    { id: 'security', icon: Shield, label: 'Security' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-5xl mx-auto px-4">
        <div className="mb-8">
          <h1 className="font-serif text-4xl font-bold text-[#1a3a5c]" data-testid="dashboard-title">My Account</h1>
          <p className="text-gray-600 mt-2">Welcome back, {user.name}</p>
        </div>

        {rewardPoints.settings?.is_enabled && (
          <div className="mb-8 bg-gradient-to-r from-[#1a3a5c] to-[#2a5a8c] p-6 rounded-lg text-white flex items-center justify-between" data-testid="rewards-card">
            <div className="flex items-center space-x-4"><Gift className="h-10 w-10 text-[#C9A967]" /><div><p className="text-sm opacity-80">Reward Points</p><p className="text-3xl font-bold">{rewardPoints.points}</p></div></div>
            <div className="text-right"><p className="text-sm opacity-80">Value</p><p className="text-xl font-semibold">₹{((rewardPoints.points || 0) * (rewardPoints.settings?.points_value_in_rupees || 0.25)).toFixed(2)}</p></div>
          </div>
        )}

        <div className="flex space-x-4 mb-8 border-b border-gray-200 overflow-x-auto">
          {tabs.map(({ id, icon: Icon, label }) => (
            <button key={id} onClick={() => setActiveTab(id)} className={`px-6 py-3 font-medium capitalize transition-colors whitespace-nowrap ${activeTab === id ? 'border-b-2 border-[#1a3a5c] text-[#1a3a5c]' : 'text-gray-600 hover:text-[#1a3a5c]'}`} data-testid={`tab-${id}`}>
              <Icon className="h-4 w-4 inline mr-2" />{label}
            </button>
          ))}
        </div>

        {activeTab === 'orders' && <OrdersSection orders={orders} cancelModal={cancelModal} setCancelModal={setCancelModal} cancellationReasons={CANCELLATION_REASONS} submitCancellationRequest={submitCancellationRequest} canRequestCancellation={canRequestCancellation} openCancelModal={openCancelModal} />}
        {activeTab === 'wishlist' && <WishlistSection wishlist={wishlist} removeFromWishlist={removeFromWishlist} />}
        {activeTab === 'addresses' && <AddressSection savedAddress={savedAddress} setSavedAddress={setSavedAddress} savingAddress={savingAddress} handleSaveAddress={handleSaveAddress} />}
        {activeTab === 'security' && <SecuritySection sessions={sessions} passwordForm={passwordForm} setPasswordForm={setPasswordForm} showPasswords={showPasswords} setShowPasswords={setShowPasswords} changingPassword={changingPassword} handleChangePassword={handleChangePassword} revokeSession={revokeSession} revokeAllOtherSessions={revokeAllOtherSessions} />}
      </div>
    </div>
  );
}
